import { Component } from '@theme/component';

/** Words that describe a range rather than name one ("Elk range" vs "Elk Mountains"). */
const GENERIC_WORDS = new Set(['range', 'ranges', 'mountains', 'mountain', 'mtns', 'mtn', 'the', 'of']);

/**
 * Peaks directory map (prototype `PeaksMap.tsx`), rendered by sections/hl-peaks-directory.liquid.
 * - Leaflet map (window.L, self-hosted assets/leaflet.js) with one marker per peak, a summit index
 *   beside it, search and range filters, a map/list toggle and an empty state.
 * - Choosing a peak (marker or index row) shows the selected-peak card. Its goods preview comes
 *   from sections/peak-preview.liquid on the peak's collection URL. It is fetched on intent
 *   (pointer over, touch or keyboard focus on a marker or row), cached per peak, shared between
 *   overlapping requests, and only shows a skeleton when the response isn't back yet.
 * - Without Leaflet the list view stays, so every peak is still a link.
 *
 * @typedef {object} Peak
 * @property {string} handle
 * @property {string} name
 * @property {number} elevation
 * @property {string} elevationText - "14,440".
 * @property {string} elevationLabel - "14,440 ft".
 * @property {string} range
 * @property {string | null} routeName
 * @property {string | null} routeClass
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} number - "01".
 * @property {string} url - The peak collection URL.
 * @property {string} search - Lowercase name, range and aliases.
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} data - Peak JSON.
 * @property {HTMLTemplateElement} attribution - Tile attribution HTML.
 * @property {HTMLTemplateElement} arrow - Arrow icon markup.
 * @property {HTMLElement} toolbar
 * @property {HTMLInputElement} search
 * @property {HTMLSelectElement} range
 * @property {HTMLButtonElement} clear
 * @property {HTMLButtonElement[]} viewButtons
 * @property {HTMLElement} status - Live region for the result count.
 * @property {HTMLElement} mapView
 * @property {HTMLElement} map
 * @property {HTMLElement} railCount
 * @property {HTMLElement} rail
 * @property {HTMLElement} selection
 * @property {HTMLElement} selectionName
 * @property {HTMLElement} selectionMeta
 * @property {HTMLAnchorElement} selectionLink
 * @property {HTMLElement} goods
 * @property {HTMLElement} listView
 * @property {HTMLUListElement} list
 * @property {HTMLElement} empty
 *
 * @extends {Component<Refs>}
 */
class PeaksMap extends Component {
  requiredRefs = ['data', 'mapView', 'map', 'rail', 'listView', 'list', 'empty', 'status'];

  /** @type {Peak[]} */
  #peaks = [];

  /** @type {Peak[]} */
  #filtered = [];

  /** @type {Peak | null} */
  #selected = null;

  /** @type {'map' | 'list'} */
  #view = 'map';

  /** @type {any} Leaflet map. */
  #map = null;

  /** @type {any} Leaflet layer group holding the visible markers. */
  #markerLayer = null;

  /** @type {Map<string, any>} Leaflet marker per peak handle. */
  #markers = new Map();

  /** @type {Map<string, HTMLButtonElement>} Index row per peak handle. */
  #rows = new Map();

  /** @type {Map<string, Promise<Element>>} Goods preview per peak URL. */
  #previews = new Map();

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #statusTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();

    try {
      this.#peaks = JSON.parse(this.refs.data.textContent || '[]');
    } catch {
      this.#peaks = [];
    }
    this.#filtered = this.#peaks;

    // Without Leaflet the server-rendered list stays as it is.
    if (!window.L || !this.#peaks.length) return;

    this.#buildRangeOptions();
    this.#buildRail();
    this.refs.toolbar.hidden = false;
    // Show the map view before Leaflet measures its container.
    this.setView('map');
    this.#initMap();
    this.#render({ reframe: false });
    this.#frame(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#statusTimer) clearTimeout(this.#statusTimer);
    this.#map?.remove();
    this.#map = null;
    this.#markers.clear();
    this.#rows.clear();
  }

  get #reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Shows the map or the list. Bound to the view buttons.
   * @param {Event | 'map' | 'list'} eventOrView
   */
  setView(eventOrView) {
    const view =
      typeof eventOrView === 'string'
        ? eventOrView
        : eventOrView.target instanceof HTMLElement && eventOrView.target.dataset.view === 'list'
          ? 'list'
          : 'map';
    this.#view = view;
    for (const button of this.refs.viewButtons ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.view === view));
    }
    this.#syncVisibility();
  }

  /** Applies the search and range filters. Bound to the search field and range select. */
  filter() {
    const query = (this.refs.search?.value ?? '').trim().toLowerCase();
    const range = this.refs.range?.value ?? '';
    // Every word must match somewhere ("elk range" finds the Elk Mountains); generic range words
    // are ignored when there's something more specific to match.
    const words = query.split(/\s+/).filter(Boolean);
    // The last word may still be mid-typing ("elk r", "elk ran"): treat the start of a generic
    // word as generic too, so the results don't jump around before "range" is finished.
    const isGeneric = (word, index) =>
      GENERIC_WORDS.has(word) ||
      (index === words.length - 1 && [...GENERIC_WORDS].some((generic) => generic.length > 3 && generic.startsWith(word)));
    const specific = words.filter((word, index) => !isGeneric(word, index));
    const terms = specific.length ? specific : words;
    this.#filtered = this.#peaks.filter(
      (peak) => (!range || peak.range === range) && terms.every((term) => peak.search.includes(term))
    );
    if (this.#selected && !this.#filtered.includes(this.#selected)) this.#select(null);
    this.#render({ reframe: true });
    this.#announce();
  }

  /** Clears search and range. Bound to "Clear filters" and the empty state button. */
  clearFilters() {
    if (this.refs.search) this.refs.search.value = '';
    if (this.refs.range) this.refs.range.value = '';
    this.filter();
    this.refs.search?.focus();
  }

  #buildRangeOptions() {
    const select = this.refs.range;
    if (!select) return;
    const ranges = [...new Set(this.#peaks.map((peak) => peak.range).filter(Boolean))].sort();
    for (const range of ranges) select.append(new Option(range, range));
  }

  #buildRail() {
    const { signal } = /** @type {AbortController} */ (this.#listeners);
    const fragment = document.createDocumentFragment();
    for (const peak of this.#peaks) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'hl-peaks-map__row';
      row.dataset.handle = peak.handle;
      row.innerHTML = `<small class="hl-peaks-map__row-number hl-label"></small><strong class="hl-peaks-map__row-name"></strong><span class="hl-peaks-map__row-elevation hl-label"></span>`;
      /** @type {HTMLElement} */ (row.children[0]).textContent = peak.number;
      /** @type {HTMLElement} */ (row.children[1]).textContent = peak.name;
      /** @type {HTMLElement} */ (row.children[2]).textContent = peak.elevationLabel;
      row.addEventListener('click', () => this.#select(peak), { signal });
      this.#addIntentListeners(row, peak, signal);
      this.#rows.set(peak.handle, row);
      fragment.append(row);
    }
    this.refs.rail.replaceChildren(fragment);
  }

  /**
   * Prefetches a peak's goods when someone is about to choose it.
   * @param {HTMLElement} element
   * @param {Peak} peak
   * @param {AbortSignal} signal
   */
  #addIntentListeners(element, peak, signal) {
    const prefetch = () => this.#loadPreview(peak).catch(() => {});
    element.addEventListener('pointerenter', prefetch, { signal });
    element.addEventListener('touchstart', prefetch, { signal, passive: true });
    element.addEventListener('focus', prefetch, { signal });
  }

  #initMap() {
    const L = window.L;
    const { signal } = /** @type {AbortController} */ (this.#listeners);

    this.#map = L.map(this.refs.map, {
      center: [39.1, -106.3],
      zoom: 7,
      minZoom: 5,
      scrollWheelZoom: false,
      keyboard: true,
      zoomAnimation: !this.#reduceMotion,
      fadeAnimation: !this.#reduceMotion,
      markerZoomAnimation: !this.#reduceMotion,
    });

    L.tileLayer(this.dataset.tileUrl || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: this.refs.attribution?.innerHTML.trim() ?? '',
      maxZoom: 17,
    }).addTo(this.#map);
    this.#map.attributionControl.setPrefix(false);

    this.#markerLayer = L.layerGroup().addTo(this.#map);

    for (const peak of this.#peaks) {
      const marker = L.marker([peak.latitude, peak.longitude], {
        title: this.#template(this.dataset.markerTitleTemplate, { name: peak.name, elevation: peak.elevationText }),
        alt: peak.name,
        keyboard: true,
        riseOnHover: true,
        icon: L.divIcon({
          className: 'hl-peaks-map__marker',
          html: '<span class="hl-peaks-map__marker-core"><svg viewBox="0 0 32 32" aria-hidden="true" focusable="false"><path d="M3 27 12 7l9 20H3Z"/><path d="m15 27 6-12 8 12H15Z"/></svg></span>',
          iconSize: [30, 33],
          iconAnchor: [15, 30],
          popupAnchor: [0, -27],
        }),
      });
      marker.bindPopup(() => this.#popupContent(peak));
      marker.on('click', () => this.#select(peak));
      marker.on('mouseover', () => this.#loadPreview(peak).catch(() => {}));
      marker.on('add', () => {
        const element = marker.getElement();
        if (!element) return;
        element.setAttribute('aria-label', marker.options.title);
        element.classList.toggle('is-selected', this.#selected === peak);
        element.addEventListener('focus', () => this.#loadPreview(peak).catch(() => {}), { signal });
        element.addEventListener('touchstart', () => this.#loadPreview(peak).catch(() => {}), {
          signal,
          passive: true,
        });
      });
      this.#markers.set(peak.handle, marker);
    }

    // The map frame changes size with the selection card and the viewport; resizing keeps the
    // center. Coming back from the list view (or the empty state) re-centers on the selection.
    let wasHidden = false;
    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.#map) return;
      if (!this.refs.map.clientWidth) {
        wasHidden = true;
        return;
      }
      if (!wasHidden) {
        this.#map.invalidateSize({ animate: false });
        return;
      }
      wasHidden = false;
      this.#map.invalidateSize({ pan: false });
      const peak = this.#selected;
      if (peak) this.#map.setView([peak.latitude, peak.longitude], Math.max(this.#map.getZoom(), 9), { animate: false });
      else this.#frame(false);
    });
    this.#resizeObserver.observe(this.refs.map);
  }

  /** @param {Peak} peak */
  #popupContent(peak) {
    const wrapper = document.createElement('div');
    const name = document.createElement('strong');
    name.className = 'hl-peaks-map__popup-name';
    name.textContent = peak.name;
    const meta = document.createElement('span');
    meta.className = 'hl-peaks-map__popup-meta';
    meta.textContent = `${peak.elevationLabel.toUpperCase()} · ${peak.range}`;
    const link = document.createElement('a');
    link.className = 'hl-peaks-map__popup-link';
    link.href = peak.url;
    link.textContent = this.dataset.popupLinkLabel ?? '';
    link.insertAdjacentHTML('beforeend', this.refs.arrow?.innerHTML ?? '');
    wrapper.append(name, meta, link);
    return wrapper;
  }

  /** @param {{ reframe: boolean }} options */
  #render({ reframe }) {
    const visible = new Set(this.#filtered.map((peak) => peak.handle));

    // Markers
    if (this.#markerLayer) {
      for (const [handle, marker] of this.#markers) {
        const shown = this.#markerLayer.hasLayer(marker);
        if (visible.has(handle) && !shown) this.#markerLayer.addLayer(marker);
        if (!visible.has(handle) && shown) this.#markerLayer.removeLayer(marker);
      }
    }

    // Summit index and list view
    for (const [handle, row] of this.#rows) row.hidden = !visible.has(handle);
    for (const item of this.refs.list.querySelectorAll('[data-handle]')) {
      if (item instanceof HTMLElement) item.hidden = !visible.has(item.dataset.handle ?? '');
    }

    if (this.refs.railCount) {
      this.refs.railCount.textContent = this.#template(this.dataset.foundTemplate, { count: this.#filtered.length });
    }

    const query = this.refs.search?.value.trim() ?? '';
    if (this.refs.clear) this.refs.clear.hidden = !query && !this.refs.range?.value;

    this.#syncVisibility();
    if (reframe) this.#frame(true);
  }

  /** Fits the map to the visible peaks (only when the result set changes, not on selection). */
  #frame(animate = true) {
    if (!this.#map || !this.#filtered.length) return;
    const L = window.L;
    const animated = animate && !this.#reduceMotion;
    if (this.#filtered.length === 1) {
      const [peak] = this.#filtered;
      if (!peak) return;
      if (animated) this.#map.flyTo([peak.latitude, peak.longitude], 10, { duration: 0.65 });
      else this.#map.setView([peak.latitude, peak.longitude], 10);
      return;
    }
    const bounds = L.latLngBounds(this.#filtered.map((peak) => [peak.latitude, peak.longitude]));
    this.#map.fitBounds(bounds, { padding: [42, 42], maxZoom: 10, animate: animated });
  }

  #syncVisibility() {
    const empty = !this.#filtered.length;
    this.refs.empty.hidden = !empty;
    this.refs.mapView.hidden = empty || this.#view !== 'map';
    this.refs.listView.hidden = empty || this.#view !== 'list';
  }

  /**
   * Selects a peak (or clears the selection) and shows its card.
   * @param {Peak | null} peak
   */
  #select(peak) {
    const previous = this.#selected;
    this.#selected = peak;

    if (previous) {
      this.#markers.get(previous.handle)?.getElement()?.classList.remove('is-selected');
      this.#rows.get(previous.handle)?.removeAttribute('aria-current');
    }

    const { selection, mapView } = this.refs;
    if (!peak) {
      if (selection) selection.hidden = true;
      mapView.classList.remove('has-selection');
      return;
    }

    this.#markers.get(peak.handle)?.getElement()?.classList.add('is-selected');
    const row = this.#rows.get(peak.handle);
    row?.setAttribute('aria-current', 'true');
    if (row) this.#scrollRailTo(row);

    this.#fillSelection(peak);
    mapView.classList.add('has-selection');

    if (this.#map) {
      const zoom = Math.max(this.#map.getZoom(), 9);
      if (this.#reduceMotion) this.#map.setView([peak.latitude, peak.longitude], zoom);
      else this.#map.flyTo([peak.latitude, peak.longitude], zoom, { duration: 0.65 });
    }

    this.#showPreview(peak);
  }

  /**
   * Scrolls the summit index (only the index, never the page) so the row is in view.
   * @param {HTMLElement} row
   */
  #scrollRailTo(row) {
    const { rail } = this.refs;
    // The index list is position: relative, so offsetTop is measured from its top.
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < rail.scrollTop) rail.scrollTop = top;
    else if (bottom > rail.scrollTop + rail.clientHeight) rail.scrollTop = bottom - rail.clientHeight;
  }

  /** @param {Peak} peak */
  #fillSelection(peak) {
    const { selection, selectionName, selectionMeta, selectionLink } = this.refs;
    if (!selection) return;
    selection.hidden = false;
    if (selectionName) selectionName.textContent = peak.name;
    if (selectionLink) selectionLink.href = peak.url;
    if (selectionMeta) {
      const lines = [peak.elevationLabel, peak.range];
      if (peak.routeName) {
        lines.push(
          this.#template(this.dataset.routeTemplate, { route: peak.routeName, class: peak.routeClass ?? '' })
            .replace(/\s*·\s*$/, '')
            .trim()
        );
      }
      selectionMeta.replaceChildren(
        ...lines.filter(Boolean).map((text) => {
          const span = document.createElement('span');
          span.textContent = text;
          return span;
        })
      );
    }
  }

  /**
   * Fetches (once) and parses a peak's goods preview.
   * @param {Peak} peak
   * @returns {Promise<Element>}
   */
  #loadPreview(peak) {
    const cached = this.#previews.get(peak.url);
    if (cached) return cached;

    const url = new URL(peak.url, window.location.origin);
    url.searchParams.set('section_id', 'peak-preview');
    const request = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Preview request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        const preview = new DOMParser().parseFromString(html, 'text/html').querySelector('.hl-peak-preview');
        if (!preview) throw new Error('Preview markup missing');
        return preview;
      })
      .catch((error) => {
        // Let the next attempt try again.
        this.#previews.delete(peak.url);
        throw error;
      });
    this.#previews.set(peak.url, request);
    return request;
  }

  /** @param {Peak} peak */
  #showPreview(peak) {
    const { goods } = this.refs;
    if (!goods) return;

    let settled = false;
    const request = this.#loadPreview(peak);

    request
      .then((preview) => {
        settled = true;
        if (this.#selected !== peak) return;
        this.#renderGoods(preview);
      })
      .catch((error) => {
        settled = true;
        if (this.#selected !== peak) return;
        console.warn('[peaks-map]', error);
        goods.replaceChildren(this.#status(this.dataset.goodsError ?? '', true));
      });

    // A cached or prefetched response renders straight away; otherwise show the skeleton.
    queueMicrotask(() => {
      if (settled || this.#selected !== peak) return;
      const skeleton = () => {
        const bar = document.createElement('div');
        bar.className = 'hl-peaks-map__skeleton';
        bar.setAttribute('aria-hidden', 'true');
        return bar;
      };
      goods.replaceChildren(this.#status(this.dataset.goodsLoading ?? ''), skeleton(), skeleton());
    });
  }

  /** @param {Element} preview */
  #renderGoods(preview) {
    const { goods } = this.refs;
    if (!goods) return;
    const count = Number(preview.getAttribute('data-count')) || 0;
    const list = preview.querySelector('.hl-peak-preview__list');
    if (!count || !list) {
      goods.replaceChildren(this.#status(this.dataset.goodsEmpty ?? ''));
      return;
    }
    const limit = Number(this.dataset.goodsLimit) || 5;
    const copy = /** @type {Element} */ (list.cloneNode(true));
    for (const item of [...copy.children].slice(limit)) item.remove();
    /** @type {Node[]} */
    const nodes = [copy];
    const shown = copy.children.length;
    if (count > shown) {
      const more = document.createElement('span');
      more.className = 'hl-peaks-map__goods-more';
      more.textContent = this.#template(this.dataset.goodsMore, { count: count - shown });
      nodes.push(more);
    }
    goods.replaceChildren(...nodes);
  }

  /**
   * @param {string} text
   * @param {boolean} [isError]
   */
  #status(text, isError = false) {
    const paragraph = document.createElement('p');
    paragraph.className = `hl-peaks-map__goods-status${isError ? ' is-error' : ''}`;
    if (isError) paragraph.setAttribute('role', 'alert');
    paragraph.textContent = text;
    return paragraph;
  }

  /** Announces the result count after typing settles. */
  #announce() {
    if (this.#statusTimer) clearTimeout(this.#statusTimer);
    this.#statusTimer = window.setTimeout(() => {
      this.refs.status.textContent = this.#template(this.dataset.foundTemplate, { count: this.#filtered.length });
    }, 500);
  }

  /**
   * Replaces [key] placeholders.
   * @param {string | undefined} template
   * @param {Record<string, string | number>} values
   */
  #template(template, values) {
    let text = template ?? '';
    for (const [key, value] of Object.entries(values)) text = text.replaceAll(`[${key}]`, String(value));
    return text;
  }
}

if (!customElements.get('peaks-map')) {
  customElements.define('peaks-map', PeaksMap);
}
